import { useQuery, useMutation } from '@apollo/client';
import { useNotifications } from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';

import { CompanyTagsMutations } from './company_tag_container';
import { recordRecentCompany } from '../../home/storage';
import {
  CompanyDetailsQuery,
  CompanyDetailsQueryVariables,
  CreateCompanyMutation,
  CreateCompanyMutationVariables,
  UpdateCompanyMutation,
  UpdateCompanyMutationVariables,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { CompanyView, CompanyViewDataProps } from '../components/company';
import { CompanyForm } from '../components/company_form';
import { companyQuery, createCompanyMutation, updateCompanyMutation } from '../graphql';

class LoadingContainer extends Loader<CompanyDetailsQuery, CompanyViewDataProps, CompanyDetailsQueryVariables> {}

export const CompanyContainer = () => {
  const { company_id } = useParams() as { company_id: string };
  const navigate = useNavigate();
  const isNewCompany = company_id === 'new';
  const notify = useNotifications();
  const [createCompany] = useMutation<CreateCompanyMutation, CreateCompanyMutationVariables>(createCompanyMutation);
  const [updateCompany] = useMutation<UpdateCompanyMutation, UpdateCompanyMutationVariables>(updateCompanyMutation);
  const result = useQuery<CompanyDetailsQuery, CompanyDetailsQueryVariables>(companyQuery, {
    variables: {
      company_id,
    },
    skip: isNewCompany,
  });

  const company = result.data?.company;
  useEffect(() => {
    if (isNil(company)) {
      return;
    }
    recordRecentCompany({
      companyId: company.company_id,
      companyName: company.company_name ?? undefined,
    });
  }, [company]);

  if (isNewCompany) {
    return (
      <CompanyForm
        data={{ company: null }}
        createCompany={async (input) => {
          const response = await createCompany(input);
          notify.success({ title: 'Company created' });
          return response;
        }}
        routeProps={{ goBack: () => navigate(-1) }}
      />
    );
  }

  return (
    <CompanyTagsMutations>
      {({ addCompanyTag, removeCompanyTag }) => (
        <LoadingContainer
          result={result}
          mapData={({ data }) => ({
            data: {
              company: data.company ?? null,
              availableTags: data.availableCompanyTags ?? [],
              identifiers:
                data.company?.companyIdentifiers.map((identifier) => ({
                  ...identifier,
                  country: identifier.discriminatorCountry ?? undefined,
                })) ?? [],
            },
          })}>
          {({ data }) => (
            <CompanyView
              data={data}
              updateCompany={async (input) => {
                const response = await updateCompany(input);
                notify.success({ title: 'Company updated' });
                return response;
              }}
              onAddCompanyTag={addCompanyTag}
              onRemoveCompanyTag={removeCompanyTag}
            />
          )}
        </LoadingContainer>
      )}
    </CompanyTagsMutations>
  );
};
